import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown } from "@bloomberg/media-ui-icons";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { createClasses } from "@bloomberg/media-ui-core";

import { Placeholder } from "@bloomberg/media-ui-article";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import styles from "./styles.module.css";
import type { SectionFrontTab } from "~types/types";
import { TodayInMarketsCategories } from "~components/TodayInMarkets/TodayInMarketsCategories";
import { TodayInMarketsContextProvider } from "~context/TodayInMarketsContext";
import { TodayInMarketsHeatmap } from "~components/TodayInMarkets/TodayInMarketsHeatmap";
import {
  deriveTodayInMarketsTabs,
  MarketCategoryKey,
} from "~utils/todayInMarketsUtils";
import { HALF_SECOND_IN_MILLISECONDS } from "~constants";
import { usePageData } from "~context/PageContext";
import { MARKETS_IS_OPEN_STORAGE_KEY } from "~constants/pagePreference";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import { useBreakpointDetectionPending } from "~hooks/useScreenBreakpoints";
import { TranslatedMessage } from "~utils/i18n";

type TodayInMarketsProps = {
  sectionFrontName: string;
  sectionFrontTab: SectionFrontTab;
};

export const TodayInMarketsGenericPlaceholder = memo(
  function TodayInMarketsGenericPlaceholderComponent() {
    return (
      <section className={styles.placeholderContainer}>
        <Placeholder>
          <div>
            {Array.from(Array(10).keys()).map((_, i) => (
              <div className={styles.placeholderRow} key={i}>
                <Placeholder.Line />
              </div>
            ))}
            {/* Extra rows only visible on mobile and tablet */}
            {Array.from(Array(10).keys()).map((_, i) => (
              <div
                className={createClasses([
                  styles.placeholderRow,
                  styles.desktopHiddenRow,
                ])}
                key={`mobile-tablet-${i}`}
              >
                <Placeholder.Line />
              </div>
            ))}
          </div>
        </Placeholder>
      </section>
    );
  }
);

export function TodayInMarkets(props: TodayInMarketsProps) {
  const { sectionFrontName, sectionFrontTab } = props;

  const { pagePreference, updatePagePreference } = usePageData();

  const isOpenPreference = useMemo(() => {
    return (pagePreference[MARKETS_IS_OPEN_STORAGE_KEY] ?? true) as boolean;
  }, [pagePreference]);

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const isBreakpointDetectionPending = useBreakpointDetectionPending();

  useEffect(() => {
    // Fires when TIM shows up, this metric will help us measure feature adoption
    // during the A/B test. It's a quick and easy way to simplify the metrics
    // we use in Statsig as well
    trackClickEvent("Market_Data", "tim_surfaces", "uiaction", "Market Data");
  }, []);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const categories = useMemo(() => {
    if (!sectionFrontTab.sections?.length) {
      return [];
    }

    return deriveTodayInMarketsTabs(
      sectionFrontTab.sections,
      sectionFrontName as MarketCategoryKey
    );
  }, [sectionFrontTab, sectionFrontName]);

  const updateIsOpenFlag = useCallback(
    (openPreference: boolean) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      timeoutRef.current = setTimeout(() => {
        setIsOpen(openPreference);

        timeoutRef.current = null;
      }, HALF_SECOND_IN_MILLISECONDS);
    },
    [timeoutRef.current]
  );

  useEffect(() => {
    setIsVisible(isOpenPreference);

    // to delay unmount
    updateIsOpenFlag(isOpenPreference);
  }, [isOpenPreference]);

  const toggleShowHide = useCallback(() => {
    if (isVisible) {
      // Start closing animation
      setIsVisible(false);

      trackClickEvent("Market_Data", "hide_tim", "click", "Market Data");

      addPageAction("todayInMarketsToggle", {
        action: "chevron_close",
      });

      updatePagePreference(MARKETS_IS_OPEN_STORAGE_KEY, false);

      // to delay unmount
      updateIsOpenFlag(false);
    } else {
      // Mount instantly
      setIsOpen(true);

      trackClickEvent("Market_Data", "show_tim", "click", "Market Data");

      addPageAction("todayInMarketsToggle", {
        action: "chevron_open",
      });

      updatePagePreference(MARKETS_IS_OPEN_STORAGE_KEY, true);

      // Animate open
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showHideToggle = createClasses([
    styles.showHideToggle,
    {
      [styles.open]: isVisible,
      [styles.close]: !isVisible,
    },
  ]);

  const sectionWrapper = createClasses([
    styles.sectionWrapper,
    {
      [styles.open]: isVisible,
      [styles.close]: !isVisible,
    },
  ]);

  if (!categories?.length) {
    return null;
  }

  return (
    <TodayInMarketsContextProvider categories={categories}>
      <div className={styles.todayInMarkets}>
        <button onClick={toggleShowHide} aria-label="toggle today in markets">
          <div className={styles.collapsibleContainer}>
            <h2
              className={createClasses([
                styles.sectionTitle,
                typographyStyles.coreHeadlineSm,
              ])}
            >
              <TranslatedMessage
                id="todayInTheMarkets"
                defaultMessage="Today in the Markets"
              />
            </h2>

            <div className={showHideToggle} role="icon-button">
              <span className={styles.chevronIcon}>
                <ChevronDown />
              </span>
            </div>
          </div>
        </button>
        <div className={sectionWrapper}>
          {isOpen && (
            <div className={styles.sectionContainer}>
              {isBreakpointDetectionPending ? (
                <TodayInMarketsGenericPlaceholder />
              ) : (
                <>
                  <TodayInMarketsCategories
                    sectionFrontName={sectionFrontName}
                    sectionFrontTabIdentifier={sectionFrontTab?.identifier}
                  />
                  <TodayInMarketsHeatmap />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </TodayInMarketsContextProvider>
  );
}
